
import React, { useState, useMemo } from 'react';
import ProductCard from '../components/ProductCard';
import { Product } from '../types';
import { ChevronDown, Filter, X } from 'lucide-react';

interface ShopProps {
  addToCart: (p: Product) => void;
  products: Product[];
}

type SortType = 'all' | 'available' | 'promo';

const Shop: React.FC<ShopProps> = ({ addToCart, products }) => {
  const [activeCategory, setActiveCategory] = useState('Tous');
  const [activeSort, setActiveSort] = useState<SortType>('all');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const categories = useMemo(() => {
    const cats = products.map(p => p.category);
    return ['Tous', ...Array.from(new Set(cats))];
  }, [products]);

  const processedProducts = useMemo(() => {
    let filtered = [...products];

    // 1. Filtre Catégorie
    if (activeCategory !== 'Tous') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }

    // 2. Filtre Type
    if (activeSort === 'available') {
      filtered = filtered.filter(p => p.stock > 0);
    } else if (activeSort === 'promo') {
      filtered = filtered.filter(p => !!p.originalPrice);
    }

    // 3. Tri Spécifique : Disponibilité > Marque > Nom
    return filtered.sort((a, b) => {
      const stockA = a.stock > 0 ? 0 : 1;
      const stockB = b.stock > 0 ? 0 : 1;
      
      if (stockA !== stockB) return stockA - stockB; // Rupture en bas
      if (a.category !== b.category) return a.category.localeCompare(b.category); // Par marque
      return a.name.localeCompare(b.name); // Alphabétique
    });
  }, [products, activeCategory, activeSort]);

  return (
    <div className="pt-32 pb-24 min-h-screen">
      <div className="container mx-auto px-6">
        {/* Desktop & Mobile Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 border-b border-gray-100 pb-8">
          <div>
            <span className="text-[11px] uppercase tracking-[0.4em] text-brand-gold mb-4 block">
              Exploration
            </span>
            <h1 className="font-serif text-4xl md:text-5xl mb-4 md:mb-0">Les Collections</h1>
          </div>

          {/* Desktop Filters */}
          <div className="hidden md:flex flex-wrap justify-end gap-x-8 gap-y-4">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-[11px] uppercase tracking-widest font-bold luxury-transition ${
                  activeCategory === cat ? 'text-brand-gold border-b border-brand-gold' : 'text-gray-400 hover:text-brand-black'
                } pb-2`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Mobile Filter Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(true)}
            className="md:hidden mt-8 flex justify-between items-center border p-4 border-gray-200 bg-white shadow-sm"
          >
            <span className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-2">
              <Filter size={14} /> Filtrer & Trier
            </span>
            <ChevronDown size={16} />
          </button>
        </div>

        {/* Status Pills / Quick Sort */}
        <div className="flex gap-4 mb-12 overflow-x-auto pb-2 no-scrollbar">
          {[
            { id: 'all', label: 'Tout voir' },
            { id: 'available', label: 'En stock' },
            { id: 'promo', label: 'Promotions' }
          ].map(btn => (
            <button
              key={btn.id}
              onClick={() => setActiveSort(btn.id as SortType)}
              className={`px-6 py-2 rounded-full text-[10px] uppercase tracking-widest font-bold whitespace-nowrap transition-all border ${
                activeSort === btn.id 
                ? 'bg-brand-black text-white border-brand-black' 
                : 'bg-white text-gray-400 border-gray-100 hover:border-brand-gold'
              }`}
            >
              {btn.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-16">
          {processedProducts.map((product) => (
            <ProductCard 
              key={product.id} 
              product={product} 
              onAddToCart={addToCart} 
            />
          ))}
        </div>

        {processedProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 font-light italic">Aucun modèle ne correspond à vos critères.</p>
          </div>
        )}
      </div>

      {/* Mobile Sidebar Filter */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] bg-brand-black/40 backdrop-blur-sm md:hidden">
          <div className="absolute right-0 top-0 h-full w-[80%] bg-white p-8 shadow-2xl animate-in slide-in-from-right duration-300">
            <div className="flex justify-between items-center mb-12">
              <h3 className="font-serif text-2xl">Filtres</h3>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2"><X size={24} /></button>
            </div>

            <div className="space-y-10">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-brand-gold mb-6">Maison / Marque</p>
                <div className="flex flex-col gap-4">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => { setActiveCategory(cat); setIsMobileMenuOpen(false); }}
                      className={`text-left text-sm uppercase tracking-widest ${activeCategory === cat ? 'font-bold text-brand-black' : 'text-gray-400'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
